import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, notification } from 'antd';
import { StopOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationType } from '../../types/notifications';
import { notificationTarget } from './notificationTarget';
import css from './NotifierToast.module.scss';

type ContactToastType = Extract<
  NotificationType,
  'contact_switched' | 'contact_switch_proposed' | 'contact_unreachable'
>;

// Switched and suggested wear the purple of every other "different contact"
// event (Retry Contact); unreachable the grey of the drawer's unreachable
// panel, since nothing more is coming for that job.
const toasts: Record<ContactToastType, { icon: React.ReactNode; action: string }> = {
  contact_switched: {
    icon: <UserSwitchOutlined style={{ color: '#7c3aed' }} />,
    action: 'View opportunity',
  },
  contact_switch_proposed: {
    icon: <UserSwitchOutlined style={{ color: '#7c3aed' }} />,
    action: 'Review suggestion',
  },
  contact_unreachable: {
    icon: <StopOutlined style={{ color: '#8c8c8c' }} />,
    action: 'View opportunity',
  },
};

/**
 * Recipient verification (D19): says when we switched an opportunity's contact,
 * suggest a different one, or found no one reachable. Title and message are the
 * backend's.
 *
 * Toasts only. The refresh-only `contact_status_changed` is the opportunities
 * list's to act on (OpportunitiesTab), since that is what owns the rows and the
 * open drawer's copy.
 */
const ContactStatusNotifier: React.FC = () => {
  const { subscribe } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // Through refs, so the subscriptions are made once: re-subscribing on a
  // navigation would drop an event waiting out the provider's debounce (see
  // OpportunitiesReadyNotifier).
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    // One subscription per type: the provider debounces per subscriber, so a
    // shared one would let a switch and an unreachable landing together show
    // only the second.
    const unsubscribes = (Object.keys(toasts) as ContactToastType[]).map((type) =>
      subscribe([type], (event) => {
        const target = notificationTarget(event);
        const key = `contact-status-${event.id}`;
        // Already open there: the drawer refetches on the same change.
        const showButton = target !== null && target !== pathnameRef.current;

        notification.info({
          key,
          message: event.title,
          description: event.message,
          placement: 'topRight',
          duration: 8,
          icon: toasts[type].icon,
          btn: showButton ? (
            <Button
              type="primary"
              size="small"
              className={css.reviewButton}
              onClick={() => {
                notification.destroy(key);
                navigateRef.current(target);
              }}
            >
              {toasts[type].action}
            </Button>
          ) : undefined,
        });
      })
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [subscribe]);

  return null;
};

export default ContactStatusNotifier;
